import React, { useEffect, useState } from 'react'
import isDark from '../isDark'
import { perCapitaLabelMaker } from '../formatters'
import classnames from 'classnames'
import { getText } from '../languages'
import './Tooltip.scss'

const minWidth = 444;

export default function Tooltip(props) {
  const {
    hoveredCounty,
    properties,
    populations,
    metricKey,
    usMapRef,
    populationKey,
    colorScale,
    isWorld,
    language,
    hideInstructions
  } = props;
  const [instructionsOpen, setInstructionsOpen] = useState(false)

  useEffect(() => {
    if (props.zoom || props.embedParams) {
      return
    }
    let timeoutId = null
    if (!hideInstructions) {
      timeoutId = setTimeout(() => {
        setInstructionsOpen(true)
      }, 300)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [props.zoom, props.embedParams, hideInstructions])

  const facts = []
  if (!props.hideCounty) {
    const countyName = isWorld
      ? getText(language, hoveredCounty.county.properties.name)
      : populations[hoveredCounty.county.properties.GEOID].CTYNAME;

    const stateId = isWorld
      ? null
      : `${hoveredCounty.county.properties.STATEFP}000`;
    if (!properties) {
      return null
    }
    const { perCapita, value, totalValue, population } = properties;
    facts.push({label: countyName, value, totalValue, perCapita, population: +population})


    if (stateId) {
      const statePopulation = populations[stateId]
      const stateData = props.regionDataByKey[statePopulation.stateAbbr]
      if (stateData && statePopulation) {
        const datum = props.dataKey ? stateData.dataMap[props.dataKey] : stateData.data[stateData.data.length - 1]
        if (datum) {
          const value = datum[`new_${metricKey}_14dayAverage`]
          const totalValue = datum[metricKey] || null;
          const population = +statePopulation[populationKey]
          const perCapita = value / population;
          facts.push({
            label: stateData.state,
            value,
            totalValue,
            perCapita,
            population,
            extraSpace: true,
          })
        }
      }
    }
  }

  const comparisonData = props.comparisonDataKey ?
    props.regionDataByKey[props.comparisonDataKey]:
    (isWorld
      ? props.regionDataByKey['world']
      : props.regionDataByKey['US'])
  const label = isWorld
    ? getText(language, 'world')
    : comparisonData.state
  const comparisonDatum = props.dataKey ? comparisonData.dataMap[props.dataKey] : comparisonData.data[comparisonData.data.length - 1]
  const comparisonValue = comparisonDatum[`new_${metricKey}_14dayAverage`]
  const comparisonTotalValue = comparisonDatum[metricKey] || null;
  // console.log(comparisonData)
  // console.log(props.comparisonDataKey, populations)
  const comparisonPopulation = isWorld
    ? comparisonData.population
    : +populations[props.comparisonDataKey ? ( props.comparisonDataKey === 'US' ? '-1' : `${comparisonData.fips}000`) : '-1'].POPESTIMATE2019
  facts.push({ label, value: comparisonValue, totalValue: comparisonTotalValue, perCapita: comparisonValue / comparisonPopulation, population: comparisonPopulation, extraSpace: true })
  const mapPosition = usMapRef.current.getBoundingClientRect()

  const windowWidth = window.innerWidth - (18 * 2);
  const smallWidthMode = windowWidth < minWidth;
  const scale = smallWidthMode ? (windowWidth / minWidth) : 1;
  const flipX = hoveredCounty.x > window.innerWidth / 2;
  const flipY = hoveredCounty.y > window.innerHeight * 0.7;

  let transform = null
  if (smallWidthMode) {
    transform = `translateX(18px) scale(${scale})`
  } else {
    transform = ''
    if (flipX) {
      transform += 'translateX(-100%) '
    }
    if (flipY) {
      transform += 'translateY(-100%) '
    }
  }
  const defaultXOffset = 'defaultXOffset' in props ? props.defaultXOffset : 15
  const offsetX = flipX ? -defaultXOffset : defaultXOffset;
  let offsetY = smallWidthMode ? 15 : 0;
  let left = hoveredCounty.x - mapPosition.x + offsetX;
  if (smallWidthMode) {
    left = 0;
  } else {
    const tooltipSpan = flipX
      ? left - minWidth
      : left + minWidth;
    if (tooltipSpan < 0) {
      left -= tooltipSpan;
      offsetY = 8;
    } else if (tooltipSpan > windowWidth) {
      left -= tooltipSpan - windowWidth;
      offsetY = 8;
    }
  }
  const tooltipStyle = {
    top: hoveredCounty.y - mapPosition.y + offsetY,
    left,
    transform,
  };

  const tooltipContentStyle = { backgroundColor: props.backgroundColor }

  if (flipX) {
    tooltipContentStyle.borderTopRightRadius = 0
  } else {
    tooltipContentStyle.borderTopLeftRadius = 0
  }

  const instructionTextKey = isWorld
    ? properties.containsSubnational
      ? 'subnationalInstructions'
      : 'tooltipInstructionsWorld'
    : 'tooltipInstructionsUS';
  return (
    <div className='tooltip' style={tooltipStyle}>
      <div className={classnames('tooltipInstructions', {open: instructionsOpen, flipX: smallWidthMode ? !flipX : flipX , hideInstructions} )}>
        {
          // getText(language, isWorld ? 'tooltipInstructionsWorld' : 'tooltipInstructionsUS')
        }
        {props.instruction || getText(language, instructionTextKey)}
      </div>
      <div className='tooltipContent' style={tooltipContentStyle}>
        {props.dateLabel ? <span className='dateLabel'>{props.dateLabel}</span> : null}

        <table>
          <tbody>
            {facts.map((fact, factIndex) => {
              const perCapitaBGColor = fact.perCapita === null || fact.perCapita === 0 ? '#ffffff' : colorScale(fact.perCapita)
              const perCapitaColor = isDark(perCapitaBGColor) ? '#fff' : null
              return (
                <React.Fragment key={factIndex}>
                  <tr className={classnames({extraSpace: fact.extraSpace})}>
                    <td colSpan='4'>
                      {fact.label}
                      {
                        props.hidePopulation ? null : <span className='population'><span className='popLabel'>{getText(language, 'population')}: </span>{fact.population === 0 ? 'No Data' : fact.population.toLocaleString(language)}</span>
                      }
                    </td>
                  </tr>
                  <tr className='line'><td colSpan='2'></td><td colSpan='2'></td></tr>
                  <tr>
                    <td className='value'>{fact.value === null ? getText(language, 'noData') : fact.value.toLocaleString(language)}</td>
                    <td className='label center'>
                      <span>
                        {`${getText(language, metricKey === 'cases' ? 'Cases' : 'Fatalities')} ${getText(language, 'inLast14Days')}`}
                      </span>
                      <br />
                      <span>
                        {
                          (fact.totalValue === null || fact.totalValue === undefined)
                            ? null
                            : (
                              `(${fact.totalValue.toLocaleString(language)} ${getText(language, 'Total')} ${getText(language, metricKey === 'cases' ? 'Cases' : 'Fatalities')})`
                            )
                        }
                      </span>
                    </td>
                    <td className='value' style={{ backgroundColor: perCapitaBGColor, color: perCapitaColor}}>{perCapitaLabelMaker(fact.perCapita, 1, language)}</td>
                    <td className='label'>
                      <span>
                        {`${getText(language, metricKey === 'cases' ? 'casesPer100k' : 'fatalitiesPer100k')} ${getText(language, 'inLast14Days')}`}
                      </span>
                    </td>
                 </tr>
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}